import type { BroadcastService } from './services/browser/BroadcastService';
import type { TrackingDebugService } from './services/TrackingDebugService';
import type { TrackingBusEventName } from './constants/trackingBusEvent';
import { TrackingTag } from './constants/trackingTag';

type EventHandler<T = unknown> = (payload: T) => void;

interface EmitOptions {
  publisher?: string;
  /** Delivery from another tab via BroadcastChannel */
  incomingCrossTab?: boolean;
}

interface ListenerEntry {
  id: number;
  tag: string;
  handler: EventHandler;
}

/**
 * In-process publish/subscribe. Optionally backed by `BroadcastService` so **this tab** can receive events
 * posted from **other tabs** on the same origin (fan-in uses the same listener map).
 *
 * Use **`emit`** for same-tab only; **`emitCrossTab`** delivers to local listeners first, then broadcasts
 * so other tabs stay in sync (e.g. `customer:resolved`). Subscriber **`tag`** strings are for debug logs
 * only — delivery order follows subscription order.
 */
export class EventBus {
  private readonly listeners = new Map<string, ListenerEntry[]>();
  private readonly broadcastUnsub?: () => void;
  private nextListenerId = 0;

  constructor(
    private readonly broadcast?: BroadcastService,
    private readonly debug?: TrackingDebugService,
  ) {
    if (this.broadcast) {
      this.broadcastUnsub = this.broadcast.onMessage((event, data) => {
        this.emit(event as TrackingBusEventName, data, {
          publisher: TrackingTag.BroadcastChannel,
          incomingCrossTab: true,
        });
      });
    }
  }

  subscribe<T = unknown>(
    event: TrackingBusEventName,
    handler: EventHandler<T>,
    subscriberTag: string = TrackingTag.Anonymous
  ): () => void {
    const id = this.nextListenerId++;
    const entry: ListenerEntry = { id, tag: subscriberTag, handler: handler as EventHandler };
    const list = this.listeners.get(event) ?? [];
    list.push(entry);
    this.listeners.set(event, list);

    this.debug?.log('EventBus', 'subscribe', { event, subscriberTag, listenerCount: list.length });

    return () => {
      const current = this.listeners.get(event);
      if (!current) {
        return;
      }
      const idx = current.findIndex((e) => e.id === id);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
      if (current.length === 0) {
        this.listeners.delete(event);
      }
    };
  }

  emit<T = unknown>(
    event: TrackingBusEventName,
    payload?: T,
    options?: EmitOptions
  ): void {
    const list = this.listeners.get(event);
    const listenerCount = list?.length ?? 0;
    const subscriberTags = list ? [...new Set(list.map((e) => e.tag))] : [];

    if (this.debug?.isEnabled()) {
      this.debug.log('EventBus', 'emit', {
        publisher: options?.publisher,
        event,
        incomingCrossTab: options?.incomingCrossTab ?? false,
        listenerCount,
        subscriberTags,
        payload: this.debug.summarizeBusPayload(event, payload),
      });
    }

    this.deliver(event, payload);
  }

  emitCrossTab<T = unknown>(
    event: TrackingBusEventName,
    payload?: T,
    options?: EmitOptions
  ): void {
    const list = this.listeners.get(event);
    const listenerCount = list?.length ?? 0;
    const subscriberTags = list ? [...new Set(list.map((e) => e.tag))] : [];

    if (this.debug?.isEnabled()) {
      this.debug.log('EventBus', 'emitCrossTab', {
        publisher: options?.publisher,
        event,
        localThenBroadcast: true,
        listenerCount,
        subscriberTags,
        payload: this.debug.summarizeBusPayload(event, payload),
      });
    }

    this.deliver(event, payload);
    this.broadcast?.postMessage(event, payload);
  }

  private deliver<T>(
    event: TrackingBusEventName,
    payload?: T
  ): void {
    const list = this.listeners.get(event);
    if (!list || list.length === 0) {
      return;
    }
    for (const { handler } of list) {
      handler(payload);
    }
  }

  dispose(): void {
    this.broadcastUnsub?.();
    this.listeners.clear();
  }
}
